import { apiRequest } from './http';
import type { ConnectorInstanceRecord, ExchangeSummary, FlowExecutionRecord, FlowRecord, PartnerRecord } from '../types/api';

export interface OperationsSnapshotResponse {
  exchanges: ExchangeSummary[];
  flows: FlowRecord[];
  flowExecutions: FlowExecutionRecord[];
  partners: PartnerRecord[];
  connectors: ConnectorInstanceRecord[];
  metrics: string;
  observability: ObservabilityDashboardSnapshot | null;
}

export async function getOperationsSnapshot(): Promise<OperationsSnapshotResponse> {
  return (await apiRequest<{ data: OperationsSnapshotResponse }>('/operations/snapshot')).data;
}

export async function getPrometheusMetrics(): Promise<string> {
  return apiRequest<string>('/metrics', {
    headers: {
      Accept: 'text/plain',
    },
  });
}

export interface ObservabilityDashboardSnapshot {
  generatedAt: string;
  operational: {
    exchanges: number;
    failures: number;
    retries: number;
    throughputPerMinute: number;
    p95LatencyMs: number;
  };
  sla: {
    deliveryDeadlineMs: number;
    responseTimeMs: number;
    availabilityTargetPercent: number;
    deliveryBreaches: number;
    responseBreaches: number;
    availabilityBreaches: number;
  };
  partner: {
    availability: Array<{ partnerId: string; availabilityPercent: number; failures: number }>;
  };
  runtime: {
    connectorHealth: Array<{ connectorId: string; status: string; latencyMs?: number; checkedAt: string }>;
    queues: {
      outboxPendingDepth: number;
      outboxPendingLagSeconds: number;
      executionReadyDepth: number;
      executionReadyLagSeconds: number;
      outboxDeadLetterDepth: number;
      executionDeadLetterDepth: number;
    };
  };
  tracing: {
    openTelemetryEnabled: boolean;
    serviceName: string;
    exporterOtlpEndpoint?: string;
    sampledTraceCount: number;
  };
  alerting: {
    sinks: Array<{ type: 'slack' | 'email' | 'webhook'; target: string; enabled: boolean }>;
    activeAlerts: Array<{ id: string; severity: string; channel: string; title: string; description: string; createdAt: string }>;
  };
  retention: {
    hotDays: number;
    warmDays: number;
    archiveDays: number;
    complianceDays: number;
    archiveStorage: string;
  };
  forensics: {
    replayAnalyses: Array<{ replayId: string; exchangeId: string; status: string; reason: string; reconstruction: string[] }>;
    historicalTraces: Array<{ spanId: string; traceId?: string; correlationId?: string; exchangeId?: string; name: string; status: string; durationMs: number }>;
  };
}

export async function getObservabilityDashboard(): Promise<ObservabilityDashboardSnapshot> {
  const response = await apiRequest<{ data: ObservabilityDashboardSnapshot }>('/observability/dashboard');
  return response.data;
}
